﻿import router from './router'
import store from '/@/store'
import { ELocalStorageKey, EUserType } from '/@/types'
import { RouteRecordRaw } from 'vue-router'
import { computed } from 'vue'
import { loginByToken } from '/@/api/manage'
import { loginSuccess } from '/@/websocket/util/config'
import {
  BackendMenuNode,
  findMenuChainByPath,
  findMenuNodeByPath,
  hasRegisteredRoutePath,
  isManageSystemRoutePath,
  normalizeMenuPath,
  readStoredHomeConfig,
  readStoredMenuList,
  resolveHomeConfigPath,
  resolveDynamicRouteComponent
} from '/@/utils/menu-runtime'

// const store = useMyStore()

const whiteList = [
  '/project',
  '/freeLogin',
  '/pilot-login',
  '/navigator',
  '/mars3d-timeline-test'
]
const PILOT_LOGIN_PATH = '/pilot-login'
const PILOT_DEV_PREVIEW_TOKEN = 'dev-preview-token'

function shouldUsePilotDevPreviewMock (path: string) {
  if (!import.meta.env.DEV || !path.startsWith('/pilot-')) {
    return false
  }
  const params = new URLSearchParams(window.location.search)
  if (params.get('pilotReal') === '1') {
    return false
  }
  if (params.get('pilotMock') === '1') {
    return true
  }
  return !(window as any).djiBridge
}

function clearPilotDevPreviewSession () {
  if (localStorage.getItem(ELocalStorageKey.Token) !== PILOT_DEV_PREVIEW_TOKEN) {
    return
  }
  localStorage.removeItem(ELocalStorageKey.Token)
  localStorage.removeItem(ELocalStorageKey.Flag)
  localStorage.removeItem(ELocalStorageKey.UserId)
  localStorage.removeItem(ELocalStorageKey.Username)
  localStorage.removeItem(ELocalStorageKey.WorkspaceId)
  localStorage.removeItem(ELocalStorageKey.WorkspaceName)
  localStorage.removeItem(ELocalStorageKey.WorkspaceDesc)
  localStorage.removeItem(ELocalStorageKey.PlatformName)
  localStorage.removeItem(ELocalStorageKey.Device)
}

function installPilotDevPreviewSession (path: string) {
  if (!path.startsWith('/pilot-')) {
    return
  }
  if (!shouldUsePilotDevPreviewMock(path)) {
    clearPilotDevPreviewSession()
    return
  }
  if (path === PILOT_LOGIN_PATH) {
    clearPilotDevPreviewSession()
    return
  }
  localStorage.setItem(ELocalStorageKey.Token, PILOT_DEV_PREVIEW_TOKEN)
  localStorage.setItem(ELocalStorageKey.Flag, EUserType.Pilot.toString())
  localStorage.setItem(ELocalStorageKey.UserId, 'dev-preview-user')
  localStorage.setItem(ELocalStorageKey.Username, '飞手预览账号')
  localStorage.setItem(ELocalStorageKey.WorkspaceId, 'dev-preview-workspace')
  localStorage.setItem(ELocalStorageKey.WorkspaceName, '低空巡查默认工作空间')
  localStorage.setItem(ELocalStorageKey.WorkspaceDesc, '本地预览工作空间')
  localStorage.setItem(ELocalStorageKey.PlatformName, '低空巡查管理平台')
  if (!localStorage.getItem(ELocalStorageKey.Device)) {
    localStorage.setItem(ELocalStorageKey.Device, JSON.stringify({
      sn: 'DRONE-PREVIEW-001',
      status: true,
      online_status: true,
      nickname: '预览飞行器',
      device_name: 'Matrice 30 预览机',
      device_callsign: '预览飞行器',
      user_id: 'dev-preview-user',
      user_callsign: '飞手预览账号',
      bound_status: false,
      model: 'Matrice 30 预览机',
      gateway_sn: 'RC-PREVIEW-001',
      domain: 0
    }))
  }
}
const NAVIGATION_PAGE_PATH = '/uavMain/mainNavi.html'

const sidebarRouters = computed(() => store.state.sidebarRouters)
const WorkspaceLayout = () => import('/@/pages/page-web/projects/workspace.vue')
const NewHomeLayout = () => import('/@/pages/page-web/newHome.vue')

function ensureDirectBackendRoute (targetPath: string) {
  const menuList = readStoredMenuList() as BackendMenuNode[]
  const matchedMenu = findMenuNodeByPath(menuList, targetPath)
  const matchedChain = findMenuChainByPath(menuList, targetPath) || []
  if (!matchedMenu?.component || matchedMenu.component === 'Layout') {
    return false
  }

  const componentLoader = resolveDynamicRouteComponent(normalizeMenuPath(matchedMenu.path), matchedMenu.component)
  if (!componentLoader) {
    console.warn('未找到后端菜单对应组件:', matchedMenu.component, targetPath)
    return false
  }

  const routeName = String(matchedMenu.name || targetPath.replace(/\//g, '_'))
  const directRouteName = `${routeName}__direct`
  const shouldUseNewHomeLayout = matchedChain.some(node => {
    const nodePath = normalizeMenuPath(node.path)
    return node.name === 'new_home' || nodePath === '/new_home'
  })

  if (router.hasRoute(directRouteName)) {
    router.removeRoute(directRouteName)
  }
  if (router.hasRoute(`${directRouteName}__view`)) {
    router.removeRoute(`${directRouteName}__view`)
  }

  if (!hasRegisteredRoutePath(router, targetPath)) {
    router.addRoute({
      path: targetPath,
      name: directRouteName,
      component: shouldUseNewHomeLayout ? NewHomeLayout : WorkspaceLayout,
      children: [
        {
          path: '',
          name: `${directRouteName}__view`,
          component: componentLoader as any
        }
      ]
    })
  }

  return hasRegisteredRoutePath(router, targetPath)
}

function ensureHomeConfigRoute (targetPath: string) {
  const homeConfig = readStoredHomeConfig()
  if (!homeConfig || Number(homeConfig.enabled) !== 1) {
    return false
  }
  const homeRoutePath = normalizeMenuPath(homeConfig.homeRoutePath || homeConfig.home_route_path || '')
  const homeComponent = String(homeConfig.homeComponent || homeConfig.home_component || '').trim()
  const normalizedTarget = normalizeMenuPath(targetPath)
  if (!homeRoutePath || homeRoutePath !== normalizedTarget || !homeComponent) {
    return false
  }
  const componentLoader = resolveDynamicRouteComponent(homeRoutePath, homeComponent)
  if (!componentLoader) {
    console.warn('角色首页组件不存在，已回退默认首页:', homeComponent, homeRoutePath)
    return false
  }
  if (hasRegisteredRoutePath(router, homeRoutePath)) {
    return true
  }
  const routeName = `role_home_config_${homeRoutePath.replace(/[^A-Za-z0-9_]/g, '_')}`
  if (router.hasRoute(routeName)) {
    router.removeRoute(routeName)
  }
  router.addRoute({
    path: homeRoutePath,
    name: routeName,
    component: isManageSystemRoutePath(homeRoutePath) ? NewHomeLayout : WorkspaceLayout,
    children: [
      {
        path: '',
        name: `${routeName}__view`,
        component: componentLoader as any
      }
    ]
  })
  return hasRegisteredRoutePath(router, homeRoutePath)
}
function ensureRouteRegistered (route: RouteRecordRaw) {
  const routeName = route.name ? String(route.name) : ''
  if (routeName && router.hasRoute(routeName)) {
    return
  }
  if (route.path && hasRegisteredRoutePath(router, String(route.path))) {
    return
  }
  router.addRoute(route)
}

async function ensureDynamicRoutes (to: any) {
  if (sidebarRouters.value.length === 0) {
    const accessRoutes = await store.dispatch('GenerateRoutes')
    accessRoutes.forEach((route: RouteRecordRaw) => {
      ensureRouteRegistered(route)
    })
  }
  if (to.matched.length > 0 || hasRegisteredRoutePath(router, to.path)) {
    return true
  }
  const normalizedPath = normalizeMenuPath(to.path)
  return ensureDirectBackendRoute(normalizedPath) || ensureHomeConfigRoute(normalizedPath)
}

export async function ensureDynamicRoutesReady (targetPath: string) {
  const normalizedTarget = normalizeMenuPath(targetPath)
  if (!normalizedTarget) {
    return false
  }
  const resolved = router.resolve(normalizedTarget)
  return ensureDynamicRoutes({
    path: normalizedTarget,
    matched: resolved.matched || []
  })
}

function hasStoredPermission (permission: string) {
  try {
    const permissions = JSON.parse(localStorage.getItem('perms') || '[]') as string[]
    return permissions.includes('*:*:*') || permissions.includes(permission)
  } catch (error) {
    return false
  }
}

router.beforeEach(async (to, _from, next) => {
  installPilotDevPreviewSession(to.path)
  // The animation workbench is intentionally standalone while its test API is developed.
  // Keep it reachable without platform login/menu state, even when another token exists.
  if (to.path === '/mars3d-timeline-test') {
    next()
    return
  }
  if (to.path.startsWith('/model-share/')) {
    next()
    return
  }
  if (localStorage.getItem(ELocalStorageKey.Token)) {
    const requiredPermission = String((to.meta as any)?.permission || '')
    if (requiredPermission && !hasStoredPermission(requiredPermission)) {
      next('/404')
      return
    }
    const flag: number = Number(localStorage.getItem(ELocalStorageKey.Flag))
    switch (flag) {
      case EUserType.Web:
        if (to.path === '/project' || to.path === '/') {
          next(false)
          window.location.assign(resolveHomeConfigPath(NAVIGATION_PAGE_PATH))
        } else {
          if (sidebarRouters.value.length > 0) {
            if (to.matched.length > 0) {
              next()
            } else {
              try {
                const hasRoute = await ensureDynamicRoutes(to)
                if (hasRoute) {
                  next({ ...to, replace: true })
                } else {
                  next('/404')
                }
              } catch (err) {
                console.error('补充动态路由失败', err)
                next('/404')
              }
            }
          } else {
            try {
              const hasRoute = await ensureDynamicRoutes(to)
              if (hasRoute) {
                next({ ...to, replace: true })
              } else {
                next('/404')
              }
            } catch (err) {
              console.error('生成路由失败', err)
              next('/404')
            }
          }
        }
        break
      case EUserType.Pilot:
        next()
        break
      default:
        next()
        break
    }
  } else {
    // 没有token
    if (import.meta.env.DEV && (to.path.startsWith('/pilot-') || to.path === '/fh2-wayline-editor')) {
      next()
    } else if (whiteList.indexOf(to.path) !== -1) {
      // 在免登录白名单，直接进入
      next()
    } else if (to.path.startsWith('/large_screen') || to.path === '/quick-wayline-planning') {
      const urlToken = to.query.token
      // 有 token → 尝试免登录
      if (urlToken) {
        try {
          const res = await loginByToken(<loginByToken>{ token: urlToken })
          if (res.code === 0) {
            loginSuccess(res)
            // 登录成功后去掉 token 参数（防止泄露）
            next({ path: to.path, query: {} })
            return
          } else {
            // 登录失败，重定向到登录页
            next('/project')
          }
        } catch (err) {
          console.error('Token 登录失败', err)
          next('/project')
        }
      } else {
        next('/project')
      }
    } else if (isManageSystemRoutePath(to.path)) {
      const urlToken = to.query.token
      // 有 token → 尝试免登录
      if (urlToken) {
        try {
          const res = await loginByToken(<loginByToken>{ token: urlToken })
          if (res.code === 0) {
            loginSuccess(res)
            const { token: _token, ...restQuery } = to.query as Record<string, any>
            // 登录成功后去掉 token 参数（防止泄露）
            next({ path: to.path, query: restQuery })
            return
          } else {
            // 登录失败，重定向到登录页
            next('/project')
          }
        } catch (err) {
          console.error('Token 登录失败', err)
          next('/project')
        }
      } else {
        next('/project')
      }
    } else {
      next('/project') // 否则全部重定向到登录页
    }
    // next()
  }
})
